import CryptoJS from "crypto-js";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
	IAuthReducerState,
	ILoggedInUserDetails,
	ILoginUserRequestData,
	IPasswordResetDetails,
	IVerifyOTPRequestData,
	IVerifyOTPResponseData
} from "../../types";
import authServices from "../services/auth.services";

const encryptionKey = String(process.env.REACT_APP_ENCRYPTION_KEY);

const initialState: IAuthReducerState = {
	loading: false,
	success: false,
	error: "",
	currentAction: "",
	unauthorizedError: false,
	passwordReset: null,
	user: {
		user_id: 0,
		username: "",
		role: 0,
		token: "",
		first_name: "",
		last_name: "",
		full_name: "",
		mode: 0,
		email: "",
		login_type: "normal",
		secure_login_enabled_user: false,
		secure_login_enabled_society: false,
		hide_data_management: false
	}
};

export const loginUser = createAsyncThunk<ILoggedInUserDetails, ILoginUserRequestData>(
	"auth/loginUser",
	authServices.handleLoginUser
);

export const logoutUser = createAsyncThunk<void>("auth/logoutUser", authServices.handleLogoutUser);

export const verifyOTP = createAsyncThunk<IVerifyOTPResponseData, IVerifyOTPRequestData>(
	"auth/verifyOTP",
	authServices.handleVerifyOTP
);

const authReducer = createSlice({
	name: "auth",
	initialState: initialState,
	reducers: {
		clearUnauthState: (state) => {
			state.unauthorizedError = false;
			state.user.token = "";
		},
		clearAuthState: (state) => {
			state.unauthorizedError = false;
			state.user = {
				user_id: 0,
				username: "",
				token: "",
				role: 0,
				first_name: "",
				last_name: "",
				full_name: "",
				mode: 0,
				email: "",
				login_type: "normal",
				secure_login_enabled_user: false,
				secure_login_enabled_society: false,
				hide_data_management: false
			};
		},
		updateProfileDetails: (
			state,
			{ payload }: PayloadAction<{ first_name: string; last_name: string; username: string }>
		) => {
			state.user = {
				...state.user,
				first_name: payload.first_name,
				last_name: payload.last_name,
				full_name: `${payload.first_name} ${payload.last_name}`.trim(),
				username: payload.username
			};
		},

		updatePasswordResetDetails: (state, { payload }: PayloadAction<IPasswordResetDetails | null>) => {
			state.passwordReset = payload;
		}
	},
	extraReducers: (builder) => {
		// LOGIN USER
		builder.addCase(loginUser.pending, (state) => {
			state.loading = true;
			state.success = false;
			state.error = "";
			state.currentAction = "auth/loginUser";
			state.unauthorizedError = false;
		});

		builder.addCase(loginUser.rejected, (state, { error }) => {
			state.loading = false;
			state.success = false;
			state.error = error.message ?? "Something went wrong";
			state.currentAction = "auth/loginUser";
			state.unauthorizedError = false;
			state.user = {
				token: "",
				user_id: 0,
				username: "",
				role: 0,
				first_name: "",
				last_name: "",
				full_name: "",
				mode: 0,
				email: "",
				login_type: "normal",
				secure_login_enabled_user: false,
				secure_login_enabled_society: false,
				hide_data_management: false
			};
		});

		builder.addCase(loginUser.fulfilled, (state, { payload }) => {
			state.loading = false;
			state.success = true;
			state.error = "";
			state.currentAction = "auth/loginUser";
			state.unauthorizedError = false;
			
			// Safely encrypt the token with error handling
			let encryptedToken = "";
			try {
				if (payload.token && payload.token.trim() !== "") {
					encryptedToken = CryptoJS.AES.encrypt(payload.token, encryptionKey).toString();
				}
			} catch (error) {
				console.error("Token encryption failed:", error);
				encryptedToken = "";
			}
			
			state.user = { ...payload, token: encryptedToken };
		});

		// LOGOUT USER
		builder.addCase(logoutUser.pending, (state) => {
			state.loading = true;
			state.success = false;
			state.error = "";
			state.currentAction = "auth/logoutUser";
			state.unauthorizedError = false;
		});

		builder.addCase(logoutUser.rejected, (state, { error }) => {
			state.loading = false;
			state.success = false;
			state.error = error.message ?? "Something went wrong";
			state.currentAction = "auth/logoutUser";
			state.unauthorizedError = false;
		});

		builder.addCase(logoutUser.fulfilled, (state) => {
			state.loading = false;
			state.success = true;
			state.error = "";
			state.currentAction = "auth/logoutUser";
			state.unauthorizedError = false;
			state.user = {
				token: "",
				user_id: 0,
				username: "",
				role: 0,
				first_name: "",
				last_name: "",
				full_name: "",
				mode: 0,
				email: "",
				login_type: "normal",
				secure_login_enabled_user: false,
				secure_login_enabled_society: false,
				hide_data_management: false
			};
		});

		// VERIFY OTP
		builder.addCase(verifyOTP.pending, (state) => {
			state.loading = true;
			state.success = false;
			state.error = "";
			state.currentAction = "auth/verifyOTP";
			state.unauthorizedError = false;
		});

		builder.addCase(verifyOTP.rejected, (state, { error }) => {
			state.loading = false;
			state.success = false;
			state.error = error.message ?? "Something went wrong";
			state.currentAction = "auth/verifyOTP";
			state.unauthorizedError = false;
		});

		builder.addCase(verifyOTP.fulfilled, (state, { payload }) => {
			state.loading = false;
			state.success = true;
			state.error = "";
			state.currentAction = "auth/verifyOTP";
			state.unauthorizedError = false;
			
			// Safely encrypt the token with error handling
			let encryptedToken = "";
			try {
				if (payload.token && payload.token.trim() !== "") {
					encryptedToken = CryptoJS.AES.encrypt(payload.token, encryptionKey).toString();
				}
			} catch (error) {
				console.error("Token encryption failed:", error);
				encryptedToken = "";
			}
			
			state.user = {
				...state.user,
				full_name: payload.full_name,
				mode: payload.mode,
				role: payload.role,
				token: encryptedToken,
				username: payload.username,
				login_type: payload.login_type
			};
		});
	}
});

export const { clearAuthState, clearUnauthState, updateProfileDetails, updatePasswordResetDetails } =
	authReducer.actions;

export default authReducer;
